import { useCallback, useMemo } from 'react'

import { Select } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { RecordTypeStatusFragment } from 'types/graphql'

import { RecordTypeStatusDisplay } from 'src/components/RecordTypeStatus/RecordTypeStatusDisplay'
import { isRenewalsMigrated, useRecordTypeStatusesQuery } from 'src/fetch/recordTypes'

type Props = {
  currentStatus?: RecordTypeStatusFragment | null | undefined
  disabledRecordTypeStatusId?: number | null
  isClearable?: boolean
  isDisabled?: boolean
  isRequired?: boolean
  label?: string
  onValueChange: (value: number | undefined) => void
  // When true and the record type has been migrated to renewals, show only
  // renewal-lifecycle statuses. When false/undefined and migrated, hide
  // renewal statuses (default for manual-update pickers like Reactivate).
  preferRenewalStatuses?: boolean
  recordTypeId: number | null | undefined
  value: number | undefined | null
  errorMessage?: string
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}
const RecordTypeStatusSelect = ({
  currentStatus,
  disabledRecordTypeStatusId,
  errorMessage,
  isClearable,
  isDisabled,
  isRequired,
  label,
  onBlur,
  onValueChange,
  preferRenewalStatuses,
  recordTypeId,
  value,
}: Props) => {
  const { data, isLoading } = useRecordTypeStatusesQuery({ recordTypeId })

  const isMigrated = isRenewalsMigrated(data?.recordType)

  const baseStatuses = useMemo(() => {
    return data?.recordType?.statuses ?? []
  }, [data?.recordType?.statuses])

  // Post-migration filtering: when preferRenewalStatuses is set and the module
  // is migrated, show only renewal statuses (for expiration pickers). Otherwise,
  // when migrated, hide renewal statuses so manual-update pickers only show legacy.
  // Pre-migration or non-migrated modules show all statuses unfiltered.
  const filteredStatuses = useMemo(() => {
    if (!isMigrated) {
      return baseStatuses
    }
    if (preferRenewalStatuses) {
      return baseStatuses.filter((s) => s.renewalStatus != null)
    }
    return baseStatuses.filter((s) => s.renewalStatus == null)
  }, [baseStatuses, isMigrated, preferRenewalStatuses])

  // Check against the filtered list so the current status is re-added even when
  // it exists in baseStatuses but was excluded by the migration filter (e.g. a
  // record on a renewal status viewed in a legacy-only picker).
  const isCurrentStatusMissing =
    !!currentStatus && !filteredStatuses.some((s) => s.id === currentStatus.id)

  const statuses = useMemo(() => {
    return [...filteredStatuses, ...(isCurrentStatusMissing ? [currentStatus] : [])]
  }, [filteredStatuses, currentStatus, isCurrentStatusMissing])

  const options: SelectOption<number>[] = useMemo(() => {
    return statuses.map((s) => ({
      label: s.name || '',
      value: s.id,
      customDisplay: () => (
        <RecordTypeStatusDisplay
          status={s}
          isArchived={s.id === currentStatus?.id && isCurrentStatusMissing}
        />
      ),
      disabled: s.id === disabledRecordTypeStatusId,
    }))
  }, [currentStatus?.id, disabledRecordTypeStatusId, isCurrentStatusMissing, statuses])

  const selectedOption = useMemo(() => options.find((o) => o.value === value), [options, value])

  const handleSelectedOptionChange = useCallback(
    (option: SelectOption<number> | undefined) => {
      onValueChange(option?.value)
    },
    [onValueChange]
  )

  const getOptionKey = useCallback((o: SelectOption<number>) => o.value, [])

  return (
    <Select
      getOptionKey={getOptionKey}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isRequired={isRequired}
      label={label}
      onSelectedOptionChange={handleSelectedOptionChange}
      options={options}
      placeholder={isLoading ? 'Loading…' : 'Select status'}
      selectedOption={selectedOption}
      errorMessage={errorMessage}
      isInvalid={!!errorMessage}
      onBlur={onBlur}
      shouldShowSelectedOptionCustomDisplay
    />
  )
}

export default RecordTypeStatusSelect
